// Автомат звонка 1:1 по протоколу v2 (docs/protocol/calls.md §5.3–§5.10).
// Чистая логика без DOM/WebRTC: входы — действия пользователя и события сервера, выходы — Out
// (отправить сигнал, запустить heartbeat, применить SDP/ICE и т.д.). RTCPeerConnection живёт в
// rtcV2.ts; здесь только то, что можно проверить юнит-тестами.

export type Phase =
  | 'idle'
  | 'creating' // `call` отправлен, ждём `created` (callId выдаёт сервер)
  | 'ringing_out'
  | 'ringing_in'
  | 'accepting' // `accept` отправлен, ждём `accepted`
  | 'connecting'
  | 'active'
  | 'ended';

export type EndReason =
  | 'declined'
  | 'busy'
  | 'timeout'
  | 'cancelled'
  | 'ended'
  | 'ice_failed'
  | 'error'
  | 'answered_elsewhere'
  | 'limit'
  | 'unknown';

/** Событие сервера v2 (поля сверх type/v/chatId/callId зависят от type). */
export interface V2Event {
  type: string;
  v?: number;
  chatId?: string;
  callId?: string;
  [k: string]: unknown;
}

export type Out =
  | { k: 'send'; body: Record<string, unknown> }
  | { k: 'phase'; phase: Phase }
  | { k: 'hb'; on: boolean }
  | { k: 'needOffer'; negId: number } // звонящий: принято, пора создавать offer
  | { k: 'remoteOffer'; negId: number; sdp: RTCSessionDescriptionInit }
  | { k: 'remoteAnswer'; sdp: RTCSessionDescriptionInit }
  | { k: 'remoteIce'; candidate: RTCIceCandidateInit | null }
  | { k: 'remoteMute'; kind: 'audio' | 'video'; muted: boolean }
  | { k: 'remoteHold'; held: boolean }
  | { k: 'glare' } // встречный вызов: наш не создан, показываем входящий победителя
  | { k: 'rejected'; reason: string } // call_rejected: chat_busy/limit/rate_limited/forbidden
  | {
      k: 'finished';
      reason: EndReason;
      detail?: string;
      /** Завершение инициировано здесь (кнопка), а не пришло от сервера. */
      local: boolean;
      wasActive: boolean;
      /** Сервер выдал callId (звонок реально существовал). */
      created: boolean;
      role: 'caller' | 'callee' | null;
    };

/** Снимок из REST (`GET /chats/{chatId}/call[s/{callId}]`). */
export interface CallSnapshot {
  callId: string;
  state: 'ringing' | 'connecting' | 'active' | 'ended';
  fromUserId: string;
  video: boolean;
  deadlineAt?: number;
  endReason?: string | null;
}

const KNOWN_REASONS = new Set<string>([
  'declined', 'busy', 'timeout', 'cancelled', 'ended', 'ice_failed', 'error', 'answered_elsewhere', 'limit',
]);

export function toEndReason(raw: unknown): EndReason {
  return typeof raw === 'string' && KNOWN_REASONS.has(raw) ? (raw as EndReason) : 'unknown';
}

const isNegId = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= 1_000_000_000;

export class CallMachine {
  phase: Phase = 'idle';
  callId: string | null = null;
  role: 'caller' | 'callee' | null = null;
  video = false;
  clientCallToken: string | null = null;
  deadlineAt = 0;
  wasActive = false;
  localHeld = false;
  remoteHeld = false;
  private ownNeg = 0;
  private remoteNeg = 0;
  private curNeg = 0;
  private pendingHangup = false;
  private stateSent = false;

  constructor(
    readonly chatId: string,
    readonly meId: string,
    private readonly emit: (o: Out) => void,
    private readonly newId: () => string,
  ) {}

  /** Номер текущих переговоров (для `ice`). */
  get negId(): number {
    return this.curNeg;
  }

  get isHangupPending(): boolean { return this.pendingHangup; }

  get isFinished(): boolean {
    return this.phase === 'ended';
  }

  /** Принудительное локальное завершение (сигнал `call` не ушёл и т.п.). */
  abort(detail: string): void {
    this.finish('error', detail, false);
  }

  // ------------------------------------------------------------ действия пользователя

  startOutgoing(video: boolean): void {
    if (this.phase !== 'idle') return;
    this.video = video;
    this.role = 'caller';
    this.clientCallToken = this.newId();
    this.setPhase('creating');
    this.send({ type: 'call', clientCallToken: this.clientCallToken, video });
  }

  /** Входящий звонок (событие `incoming`/глэр/снимок). */
  receiveIncoming(callId: string, video: boolean, deadlineAt = 0): void {
    if (this.phase !== 'idle') return;
    this.callId = callId;
    this.video = video;
    this.deadlineAt = deadlineAt;
    this.role = 'callee';
    this.setPhase('ringing_in');
  }

  accept(): void {
    if (this.phase !== 'ringing_in' || !this.callId) return;
    this.setPhase('accepting');
    this.send({ type: 'accept', callId: this.callId });
  }

  decline(): void {
    if (this.phase !== 'ringing_in' || !this.callId) return;
    this.send({ type: 'decline', callId: this.callId });
    this.finish('declined', undefined, true);
  }

  /** Завершение из любой фазы. */
  hangup(reason: 'ended' | 'ice_failed' | 'error' = 'ended'): void {
    switch (this.phase) {
      case 'idle':
      case 'ended':
        return;
      case 'creating':
      case 'accepting':
        // callId ещё не подтверждён / сессия не принята сервером: завершим, как только узнаем.
        this.pendingHangup = true;
        return;
      case 'ringing_in':
        this.decline();
        return;
      case 'ringing_out':
        this.send({ type: 'end', callId: this.callId });
        this.finish('cancelled', undefined, true);
        return;
      default:
        this.send({ type: 'end', callId: this.callId, reason });
        this.finish(reason, undefined, true);
    }
  }

  /** Автоматическая реакция «занят другим звонком» на входящий (до показа пользователю). */
  static busyReply(callId: string): Record<string, unknown> {
    return { type: 'unavailable', callId };
  }

  heartbeat(): void {
    if (!this.callId || this.phase === 'ended' || this.phase === 'idle' || this.phase === 'creating') return;
    // hb допустим только сессиям, числящимся в звонке: звонящий — с создания, вызываемый — после accept
    if (this.role === 'callee' && (this.phase === 'ringing_in' || this.phase === 'accepting')) return;
    this.send({ type: 'hb', callId: this.callId });
  }

  sendOffer(sdp: RTCSessionDescriptionInit): number {
    this.ownNeg = Math.max(this.ownNeg, this.remoteNeg) + 1;
    this.curNeg = this.ownNeg;
    this.send({ type: 'offer', callId: this.callId, negId: this.ownNeg, sdp: sdp.sdp });
    return this.ownNeg;
  }

  sendAnswer(negId: number, sdp: RTCSessionDescriptionInit): void {
    this.send({ type: 'answer', callId: this.callId, negId, sdp: sdp.sdp });
  }

  sendIce(candidate: RTCIceCandidateInit | null): void {
    if (!this.callId || this.curNeg === 0) return;
    this.send({ type: 'ice', callId: this.callId, negId: this.curNeg, candidate });
  }

  /** Медиа-соединение установилось: заявляем серверу (останавливает дедлайн `connecting`). */
  mediaConnected(): void {
    if (this.stateSent || !this.callId || (this.phase !== 'connecting' && this.phase !== 'active')) return;
    this.stateSent = true;
    this.send({ type: 'state', callId: this.callId, connected: true });
  }

  sendMute(kind: 'audio' | 'video', muted: boolean): void {
    if (!this.callId || (this.phase !== 'connecting' && this.phase !== 'active')) return;
    this.send({ type: 'mute-state', callId: this.callId, kind, muted });
  }

  setHeld(held: boolean): void {
    if (!this.callId || this.phase !== 'active' || this.localHeld === held) return;
    this.localHeld = held;
    this.send({ type: held ? 'call_hold' : 'call_resume', callId: this.callId });
  }

  announceHoldState(): void {
    if (this.localHeld && this.callId && this.phase === 'active') {
      this.send({ type: 'call_hold', callId: this.callId });
    }
  }

  // -------------------------------------------------------------- события сервера

  onEvent(ev: V2Event): void {
    if (ev.v !== 2 || (ev.chatId && ev.chatId !== this.chatId) || this.phase === 'ended') return;
    switch (ev.type) {
      case 'created': return this.onCreated(ev);
      case 'incoming': return this.onIncoming(ev);
      case 'glare': return this.onGlare(ev);
      case 'call_rejected': return this.onCallRejected(ev);
      case 'accepted': return this.onAccepted(ev);
      case 'incoming_closed': return this.onClosedElsewhere(ev, toEndReason(ev.reason));
      case 'ended_elsewhere': return this.onClosedElsewhere(ev, 'answered_elsewhere');
      case 'offer': return this.onOffer(ev);
      case 'answer': return this.onAnswer(ev);
      case 'ice': return this.onIce(ev);
      case 'state': return this.onState(ev);
      case 'mute-state': return this.onMute(ev);
      case 'call_hold': return this.onRemoteHold(true, ev);
      case 'call_resume': return this.onRemoteHold(false, ev);
      case 'ended': return this.onEnded(ev);
      default: return;
    }
  }

  /** Результат REST-снимка (null = 404, звонка нет). Возвращение в приложение / реконнект WS. */
  applySnapshot(snap: CallSnapshot | null): void {
    if (this.phase === 'ended' || this.phase === 'idle' || this.phase === 'creating') return;
    if (!snap || snap.callId !== this.callId) {
      // Сервер этот звонок не знает: событие `ended` потеряно вместе с WS.
      this.finish('error', 'lost', false);
      return;
    }
    if (snap.state === 'ended') {
      this.finish(toEndReason(snap.endReason), undefined, false);
    } else if (this.phase === 'ringing_in' && (snap.state === 'connecting' || snap.state === 'active')) {
      // звонок принят другой сессией, а incoming_closed потерян
      this.finish('answered_elsewhere', undefined, false);
    } else if (snap.state === 'active' && this.phase === 'connecting') {
      this.wasActive = true;
      this.setPhase('active');
    }
  }

  // ---------------------------------------------------------------- обработчики

  private mine(ev: V2Event): boolean {
    return !!this.callId && ev.callId === this.callId;
  }

  private onCreated(ev: V2Event): void {
    if (this.phase !== 'creating' || ev.clientCallToken !== this.clientCallToken || typeof ev.callId !== 'string') return;
    this.callId = ev.callId;
    if (typeof ev.deadlineAt === 'number') this.deadlineAt = ev.deadlineAt;
    this.emit({ k: 'hb', on: true });
    if (ev.state === 'connecting' || ev.state === 'active') {
      this.setPhase('connecting'); // повтор токена после accept — крайне редкий случай
    } else {
      this.setPhase('ringing_out');
    }
    if (this.pendingHangup) {
      this.pendingHangup = false;
      this.hangup();
    }
  }

  private onIncoming(ev: V2Event): void {
    if (ev.fromUserId === this.meId || typeof ev.callId !== 'string') return;
    const video = ev.video === true;
    const deadline = typeof ev.deadlineAt === 'number' ? ev.deadlineAt : 0;
    if (this.phase === 'idle') {
      this.receiveIncoming(ev.callId, video, deadline);
    } else if (this.phase === 'ringing_in' && this.callId === ev.callId) {
      // incoming победителя глэра мог прийти после glare: уточняем параметры звонка
      this.video = video;
      if (deadline) this.deadlineAt = deadline;
    } else if (this.phase === 'creating') {
      this.becomeGlareLoser(ev.callId, video, deadline);
    }
    // в остальных фазах (в т.ч. тот же callId повторно) — игнор
  }

  private onGlare(ev: V2Event): void {
    if (this.phase !== 'creating' || typeof ev.callId !== 'string') return;
    this.becomeGlareLoser(ev.callId, this.video, this.deadlineAt);
  }

  /** Побеждает звонок, записанный первым: наш `call` не создал звонка, показываем входящий. */
  private becomeGlareLoser(callId: string, video: boolean, deadlineAt: number): void {
    this.pendingHangup = false;
    this.clientCallToken = null;
    this.callId = callId;
    this.video = video;
    this.deadlineAt = deadlineAt;
    this.role = 'callee';
    this.setPhase('ringing_in');
    this.emit({ k: 'glare' });
  }

  private onCallRejected(ev: V2Event): void {
    if (this.phase !== 'creating') return;
    const reason = typeof ev.reason === 'string' ? ev.reason : 'unknown';
    this.emit({ k: 'rejected', reason });
    this.finish('error', reason, false);
  }

  private onAccepted(ev: V2Event): void {
    if (!this.mine(ev)) return;
    if (ev.role === 'caller' && this.role === 'caller' && this.phase === 'ringing_out') {
      this.setPhase('connecting');
      if (this.pendingHangup) {
        this.pendingHangup = false;
        this.hangup();
        return;
      }
      this.emit({ k: 'needOffer', negId: this.ownNeg + 1 });
    } else if (ev.role === 'callee' && this.role === 'callee' && this.phase === 'accepting') {
      this.setPhase('connecting');
      this.emit({ k: 'hb', on: true });
      if (this.pendingHangup) {
        this.pendingHangup = false;
        this.hangup();
      }
    }
  }

  private onClosedElsewhere(ev: V2Event, reason: EndReason): void {
    if (!this.mine(ev) || (this.phase !== 'ringing_in' && this.phase !== 'accepting')) return;
    this.finish(reason, undefined, false);
  }

  private onOffer(ev: V2Event): void {
    if (!this.mine(ev) || this.role !== 'callee') return;
    if (this.phase !== 'connecting' && this.phase !== 'active') return;
    const sdp = ev.sdp;
    if (!isNegId(ev.negId) || typeof sdp !== 'string' || ev.negId <= this.remoteNeg) return;
    this.remoteNeg = ev.negId;
    this.curNeg = ev.negId;
    this.emit({ k: 'remoteOffer', negId: ev.negId, sdp: { type: 'offer', sdp } });
  }

  private onAnswer(ev: V2Event): void {
    if (!this.mine(ev) || this.role !== 'caller') return;
    if (this.phase !== 'connecting' && this.phase !== 'active') return;
    // answer принимается только на текущие переговоры (§5.10)
    if (!isNegId(ev.negId) || ev.negId !== this.ownNeg || typeof ev.sdp !== 'string') return;
    this.emit({ k: 'remoteAnswer', sdp: { type: 'answer', sdp: ev.sdp } });
  }

  private onIce(ev: V2Event): void {
    if (!this.mine(ev) || (this.phase !== 'connecting' && this.phase !== 'active')) return;
    if (!isNegId(ev.negId) || ev.negId !== this.curNeg) return;
    const c = ev.candidate;
    if (c == null) {
      this.emit({ k: 'remoteIce', candidate: null });
    } else if (typeof c === 'object') {
      this.emit({ k: 'remoteIce', candidate: c as RTCIceCandidateInit });
    }
  }

  private onState(ev: V2Event): void {
    if (!this.mine(ev) || ev.state !== 'active' || this.phase !== 'connecting') return;
    this.wasActive = true;
    this.setPhase('active');
  }

  private onMute(ev: V2Event): void {
    if (!this.mine(ev)) return;
    if ((ev.kind === 'audio' || ev.kind === 'video') && typeof ev.muted === 'boolean') {
      this.emit({ k: 'remoteMute', kind: ev.kind, muted: ev.muted });
    }
  }

  private onRemoteHold(held: boolean, ev: V2Event): void {
    if (!this.mine(ev) || (this.phase !== 'connecting' && this.phase !== 'active')) return;
    this.remoteHeld = held;
    this.emit({ k: 'remoteHold', held });
  }

  private onEnded(ev: V2Event): void {
    if (typeof ev.callId === 'string') {
      if (!this.mine(ev)) return;
    } else if (this.phase !== 'creating') {
      return; // `ended` без callId (unsupported_peer) относится только к нашему `call`
    }
    const detail = typeof ev.detail === 'string' ? ev.detail : undefined;
    this.finish(toEndReason(ev.reason), detail, false);
  }

  // ---------------------------------------------------------------------- helpers

  private setPhase(p: Phase): void {
    this.phase = p;
    this.emit({ k: 'phase', phase: p });
  }

  private send(body: Record<string, unknown>): void {
    this.emit({ k: 'send', body });
  }

  private finish(reason: EndReason, detail: string | undefined, local: boolean): void {
    if (this.phase === 'ended') return;
    this.emit({ k: 'hb', on: false });
    this.localHeld = false;
    this.remoteHeld = false;
    this.phase = 'ended';
    this.emit({ k: 'phase', phase: 'ended' });
    this.emit({ k: 'finished', reason, detail, local, wasActive: this.wasActive, created: this.callId !== null, role: this.role });
  }
}
